import React, {FC, useRef, useState} from "react"
import Papa from "papaparse"
import PropTypes from "prop-types"
import {chunk, uniq} from "lodash"

import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import History from "@irati/utils/history"
import Flex from "@irati/ui/src/Flex"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import {Col, Row} from "react-bootstrap"
import Loading from "@irati/ui/src/Loading"
import DragAndDrop from "@irati/ui/src/DragAndDrop"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import usersService from "@irati/identity/services/users"
import teamsService from "@irati/identity/services/teams"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {TeamAssignment, TeamState} from "@irati/types"
import {promiseSerialAll} from "@irati/utils/promise"

import makeCSV from "@irati/utils/csv/makeCSV"
import {CircledConnector} from "../components/Users/AddUsers/AddPeopleCSV/styled"
import DownloadUsersExport from "../components/Teams/DownloadUsersExport"
import FieldsRecap from "../components/Users/AssignManagers/FieldsRecap"
import {VIEW_TEAM} from "../routes/routeConfig"

interface Props {}

const BulkUpdateTeams: FC<Props> = (_, context) => {
  const auth = useAuth()
  const {formatMessage} = useIntl()
  const [showSuccess, setShowSuccess] = useState(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [failedUsers, setFailedUsers] = useState<string[]>([])
  const [failedTeams, setFailedTeams] = useState<string[]>([])
  const [successfulUsers, setSuccessfulUsers] = useState(0)
  const downloadUsersRef = useRef()

  const onDrop = (acceptedFiles: any) => {
    setShowSuccess(false)
    setSuccessfulUsers(0)
    setFailedUsers([])
    setFailedTeams([])
    Papa.parse(acceptedFiles[0], {
      header: false,
      skipEmptyLines: true,
      complete: (res) => {
        const teamsThatFailed: string[] = []
        const usersThatFailed: string[] = []
        let usersSuccessfullyUpdated = 0

        const uploading = teamsService.list(auth.business.uuid, {state: TeamState.Generic}).then(async (teams) => {
          const allTeamIds = teams.map(({uuid}) => uuid)
          const [_headers, ...data] = res.data
          const userChunks = chunk(data, 20)

          const funcs = userChunks.map((userChunk) => () => {
            return Promise.all(
              userChunk.map((user: string[]) => {
                const emailFromCSV = user?.[2]
                const teamsFromCSV = user?.[3]

                if (!emailFromCSV) {
                  return usersThatFailed.push(emailFromCSV)
                }
                const userTeams = teamsFromCSV
                  ? teamsFromCSV
                      .replace(", ", ",")
                      .split(",")
                      .filter((u) => !!u)
                  : []

                return usersService.getUserByEmail(emailFromCSV).then((currentUser) => {
                  if (!currentUser) {
                    return usersThatFailed.push(emailFromCSV)
                  }
                  const usersCurrentTeamIds = currentUser?.teamIds.filter((id) => allTeamIds.includes(id)) || []
                  const teamIds = userTeams.reduce((current: string[] | boolean, key: string) => {
                    const foundTeam = teams?.find(({teamName}) => {
                      return teamName === key.trim()
                    })

                    if (foundTeam) {
                      return [...current, foundTeam.uuid]
                    }
                    teamsThatFailed.push(key)
                    return current
                  }, [])

                  const addPromises = teamIds.map((id: string) => {
                    if (!currentUser?.teamIds?.includes(id)) {
                      return teamsService.addUserToTeam(id, currentUser.uuid, TeamAssignment.Basic)
                    }
                    return Promise.resolve()
                  })
                  const removePromises = usersCurrentTeamIds.map((id) => {
                    if (!teamIds?.includes(id)) {
                      return teamsService.removeUserFromTeam(id, currentUser.uuid, TeamAssignment.Basic)
                    }
                    return Promise.resolve()
                  })
                  return Promise.all([...addPromises, ...removePromises]).then(() => {
                    usersSuccessfullyUpdated++
                  })
                })
              })
            )
          })
          await promiseSerialAll(funcs)

          if (teamsThatFailed?.length || usersThatFailed?.length) {
            setFailedTeams(teamsThatFailed)
            setFailedUsers(usersThatFailed)
          }
          if (usersSuccessfullyUpdated > 0) {
            setSuccessfulUsers(usersSuccessfullyUpdated)
            setShowSuccess(true)
          }
        })
        setLoad(uploading)
      },
      error: (res) => {
        console.log("[parsingCSV] Error: ", res)
      }
    })
  }

  const handleGoBackClick = () => {
    History.push(VIEW_TEAM, context.apps.ADMIN)
  }

  const failedTeamText = () => {
    if (failedTeams?.length > 5) {
      const firstFiveTeams = uniq(failedTeams).slice(0, 5)
      return firstFiveTeams
        .map((team) => `* ${team}`)
        .join("\n")
        .concat(
          ` ${formatMessage({id: "admin.components.bulkUpdateTeams.alerts.teamsError.errorText"}, {teams: failedTeams?.length - 5})}`
        )
    }
    return uniq(failedTeams)
      .map((team) => `* ${team}`)
      .join("\n")
  }

  const downloadFailedTeams = () => {
    const headers = [formatMessage({id: "admin.components.bulkUpdateTeams.alerts.teamsError.header"})]
    const csv = Papa.unparse({fields: headers, data: failedTeams.map((failedTeam) => [failedTeam])})
    makeCSV(csv, "failedTeams.csv")
  }

  const downloadFailedUsers = () => {
    const headers = [formatMessage({id: "admin.components.bulkUpdateTeams.alerts.userError.header"})]
    const csv = Papa.unparse({fields: headers, data: failedUsers.map((failedUser) => [failedUser])})
    makeCSV(csv, "failedUsers.csv")
  }

  return (
    <>
      <Button tertiary onClick={handleGoBackClick} marginBottom={2}>
        <Icon icon={IconTypes.chevronLeft} marginRight />
        {formatMessage({id: "admin.teams.backToTeams.label"})}
      </Button>
      <Flex justifyContent="flex-start" direction="column">
        <H2 marginBottom={4}>
          <FormattedMessage id="admin.components.bulkUpdateTeams.title" />
        </H2>
        <Alert type={AlertTypes.warning} title="admin.components.bulkUpdateTeams.alert" nonDismissable />
        <H4 marginBottom={2}>
          <FormattedMessage id="admin.components.bulkUpdateTeams.subTitle" />
        </H4>
        <CircledConnector
          data={["renderCompFromProps", "renderCompFromProps", "renderCompFromProps"]}
          comp={[
            <div key="downloadUsers">
              <DownloadUsersExport ref={downloadUsersRef} />
              <Body small>
                <FormattedMessage id="admin.components.bulkUpdateTeams.circledConnector.fieldList" />
              </Body>
            </div>,
            <div key="updateTeamsinCSV">
              <Body>
                <FormattedMessage id="admin.components.bulkUpdateTeams.circledConnector.update" />
              </Body>
              <Body small>
                <FormattedMessage id="admin.components.bulkUpdateTeams.circledConnector.assign" />
              </Body>
            </div>,
            <Body key="uploadCompletedCSV">
              <FormattedMessage id="admin.components.bulkUpdateTeams.circledConnector.upload" />
            </Body>
          ]}
        />
        <Row>
          <Col md={12}>
            <Loading load={load} />
            {showSuccess && (
              <Alert
                type={AlertTypes.success}
                title="admin.components.bulkUpdateTeams.alerts.success.title"
                subTitle={formatMessage({id: "admin.components.bulkUpdateTeams.alerts.success.subTitle"}, {successfulUsers})}
                onHide={() => setShowSuccess(false)}
                link="admin.components.bulkUpdateTeams.alerts.success.link"
                onClickLink={() => downloadUsersRef.current.download()}
              />
            )}
            {failedUsers?.length > 0 && (
              <Alert
                type={AlertTypes.error}
                title="admin.components.bulkUpdateTeams.alerts.userError.title"
                subTitle={formatMessage(
                  {id: "admin.components.bulkUpdateTeams.alerts.userError.subTitle"},
                  {failedUsers: failedUsers?.length}
                )}
                onHide={() => setFailedUsers([])}
                link="admin.components.bulkUpdateTeams.alerts.userError.link"
                onClickLink={downloadFailedUsers}
              />
            )}
            {failedTeams?.length > 0 && (
              <Alert
                type={AlertTypes.error}
                title="admin.components.bulkUpdateTeams.alerts.teamsError.title"
                subTitle={formatMessage(
                  {id: "admin.components.bulkUpdateTeams.alerts.teamsError.subTitle"},
                  {
                    teams: failedTeamText()
                  }
                )}
                onHide={() => setFailedTeams([])}
                link={failedTeams?.length > 5 ? "admin.components.bulkUpdateTeams.alerts.teamsError.link" : undefined}
                onClickLink={downloadFailedTeams}
              />
            )}
            <DragAndDrop onDrop={onDrop} />
            <FieldsRecap
              fields={[
                {
                  label: "admin.components.bulkUpdateTeams.circledConnector.fields.firstName.label",
                  description: "admin.components.bulkUpdateTeams.circledConnector.fields.firstName.desc"
                },
                {
                  label: "admin.components.bulkUpdateTeams.circledConnector.fields.lastName.label",
                  description: "admin.components.bulkUpdateTeams.circledConnector.fields.lastName.desc"
                },
                {
                  label: "admin.components.bulkUpdateTeams.circledConnector.fields.email.label",
                  description: "admin.components.bulkUpdateTeams.circledConnector.fields.email.desc"
                },
                {
                  label: "admin.components.bulkUpdateTeams.circledConnector.fields.teams.label",
                  description: "admin.components.bulkUpdateTeams.circledConnector.fields.teams.desc"
                }
              ]}
              bottomText="admin.components.bulkUpdateTeams.circledConnector.bottomText"
            />
          </Col>
        </Row>
      </Flex>
    </>
  )
}

BulkUpdateTeams.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default BulkUpdateTeams
